import React, { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";

/**
 * Parses simple CSV / pasted-spreadsheet content. Header row required.
 * Expected columns (case-insensitive, ; oder , separator):
 *   asset_id, asset_name, disposal_date (YYYY-MM-DD), disposal_proceeds, proceeds_account_id, gain_loss_account_id
 */
function parseCsv(text) {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return { rows: [], error: "Mindestens 1 Header + 1 Datenzeile erforderlich." };
  const sep = lines[0].includes(";") ? ";" : ",";
  const header = lines[0].split(sep).map((h) => h.trim().toLowerCase());
  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cols = lines[i].split(sep).map((c) => c.trim());
    const row = {};
    header.forEach((h, idx) => {
      const v = cols[idx];
      if (h === "disposal_proceeds") row[h] = v ? Number(v.replace(",", ".")) : 0;
      else if (v) row[h] = v;
    });
    if (!row.disposal_date) return { rows: [], error: `Zeile ${i + 1}: disposal_date fehlt.` };
    if (!row.asset_id && !row.asset_name) {
      return { rows: [], error: `Zeile ${i + 1}: asset_id ODER asset_name muss gesetzt sein.` };
    }
    rows.push(row);
  }
  return { rows };
}

export function BulkDisposeDialog({ open, onClose, companyId, onSuccess }) {
  const [csvText, setCsvText] = useState(
    "asset_name;disposal_date;disposal_proceeds;proceeds_account_id;gain_loss_account_id\n"
  );
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setError("");
    setResult(null);
    const { rows, error: parseError } = parseCsv(csvText);
    if (parseError) {
      setError(parseError);
      return;
    }
    if (rows.length === 0) {
      setError("Keine Datenzeilen gefunden.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/assets/bulk-dispose`, rows);
      setResult(r.data);
      if (r.data.successful > 0) onSuccess?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Fehler beim Bulk-Abgang.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="bulk-dispose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Bulk-Abgang aus CSV
          </DialogTitle>
          <DialogDescription>
            Mehrere Anlagen in einem Schritt abgehen. CSV muss Header-Zeile haben.
            Spalten (Reihenfolge egal): <code>asset_id</code> oder <code>asset_name</code>,{" "}
            <code>disposal_date</code> (YYYY-MM-DD), <code>disposal_proceeds</code>,{" "}
            <code>proceeds_account_id</code>, <code>gain_loss_account_id</code>. Trenner: <code>;</code> oder <code>,</code>.
          </DialogDescription>
        </DialogHeader>

        <textarea
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          rows={8}
          className="w-full font-mono text-xs rounded-md border border-slate-200 bg-slate-50 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-400"
          placeholder="asset_name;disposal_date;disposal_proceeds;proceeds_account_id;gain_loss_account_id&#10;Laptop XY;2026-06-30;500;1020;6800"
          data-testid="bulk-dispose-csv-input"
          spellCheck={false}
        />

        {error && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="bulk-dispose-error">
            {error}
          </div>
        )}

        {result && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs space-y-1" data-testid="bulk-dispose-result">
            <div className="font-semibold">
              {result.successful} von {result.total_rows} erfolgreich
              {result.failed > 0 && ` · ${result.failed} fehlgeschlagen`}
            </div>
            <ul className="space-y-0.5 max-h-40 overflow-y-auto">
              {result.results.map((r, i) => (
                <li key={i} className={r.success ? "text-emerald-700" : "text-rose-700"}>
                  [{r.success ? "OK" : "FAIL"}] {r.asset_name}
                  {r.error && <span className="text-slate-500"> — {r.error}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} data-testid="bulk-dispose-cancel">
            Schliessen
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={submitting} data-testid="bulk-dispose-submit">
            {submitting ? "Bucht ..." : "Bulk-Abgang ausführen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
